import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  VStack,
  HStack,
  Text,
  Switch,
  FormHelperText,
  Grid,
  List,
  ListItem,
  Spinner,
  useOutsideClick,
} from '@chakra-ui/react'
import {
  createAssignedDeviceSchema,
  type CreateAssignedDeviceFormData,
} from '@/lib/validations/auth'
import { apiClient } from '@/lib/api/client'
import { useToastNotification } from '@/lib/utils/useToastNotification'

interface AssignedDeviceFormProps {
  deviceId?: number
  onSuccess?: () => void
  onCancel?: () => void
}

interface UserOption {
  id: number
  user_login?: string
  first_name?: string
  last_name?: string
  employee_id?: string
  position?: string
}

function userLabel(u: UserOption) {
  const emp = u.employee_id || ''
  const name = [u.first_name, u.last_name].filter(Boolean).join(' ')
  const pos = u.position || ''
  return [emp, name, pos].filter(Boolean).join(' - ')
}

export function AssignedDeviceForm({ deviceId, onSuccess, onCancel }: AssignedDeviceFormProps) {
  const { showError, showSuccess } = useToastNotification()
  const [userSearch, setUserSearch] = useState('')
  const [userResults, setUserResults] = useState<UserOption[]>([])
  const [userSearching, setUserSearching] = useState(false)
  const [selectedUser, setSelectedUser] = useState<UserOption | null>(null)
  const [showDropdown, setShowDropdown] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isEditMode = !!deviceId

  useOutsideClick({ ref: dropdownRef, handler: () => setShowDropdown(false) })

  const runUserSearch = (term: string) => {
    if (!term || term.length < 3) {
      setUserResults([])
      setUserSearching(false)
      return
    }
    setUserSearching(true)
    apiClient
      .get<UserOption[]>(`/options/users/search?search=${encodeURIComponent(term)}`)
      .then((res) => {
        const data = Array.isArray(res.data) ? res.data : (res.data as any)?.data ?? []
        setUserResults(data)
      })
      .catch(() => setUserResults([]))
      .finally(() => setUserSearching(false))
  }

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<CreateAssignedDeviceFormData>({
    resolver: zodResolver(createAssignedDeviceSchema),
    defaultValues: {
      user_id: undefined as any,
      model: '',
      brand: '',
      serial_no: '',
      os: '',
      os_version: undefined,
      ram_size: '',
      storage_size: '',
      android_id: '',
      active: true,
    },
  })

  const active = watch('active')

  useEffect(() => {
    if (isEditMode && deviceId) {
      let cancelled = false
      const load = async () => {
        try {
          const res = await apiClient.get(`/user-management/assigned-devices/${deviceId}`)
          const d = (res.data as any)?.data ?? res.data ?? {}
          reset({
            user_id: d.user_id ?? undefined,
            model: d.model ?? '',
            brand: d.brand ?? '',
            serial_no: d.serial_no ?? '',
            os: d.os ?? '',
            os_version: d.os_version ?? undefined,
            ram_size: d.ram_size ?? '',
            storage_size: d.storage_size ?? '',
            android_id: d.android_id ?? '',
            active: d.active === 'Active' || d.active === true,
          })
          if (d.user_full_name || d.user) {
            const u = d.user || {}
            setSelectedUser({
              id: d.user_id,
              first_name: u.first_name,
              last_name: u.last_name,
              employee_id: u.employee_id,
              position: u.position,
            })
            setUserSearch(userLabel({ id: d.user_id, ...u }) || d.user_full_name || '')
          }
        } catch (e) {
          if (!cancelled) showError(e)
        }
      }
      load()
      return () => { cancelled = true }
    }
  }, [deviceId, isEditMode, reset, showError])

  const onUserSelect = (u: UserOption) => {
    setSelectedUser(u)
    setUserSearch(userLabel(u))
    setValue('user_id', u.id, { shouldValidate: true })
    setShowDropdown(false)
    setUserResults([])
  }

  const onSubmit = async (data: CreateAssignedDeviceFormData) => {
    setIsSubmitting(true)
    try {
      const payload = {
        ...data,
        os_version: data.os_version != null && !Number.isNaN(Number(data.os_version)) ? Number(data.os_version) : null,
      }
      if (isEditMode && deviceId) {
        await apiClient.put(`/user-management/assigned-devices/${deviceId}`, payload)
        showSuccess('Success', 'Assigned device updated successfully')
      } else {
        await apiClient.post('/user-management/assigned-devices', payload)
        showSuccess('Success', 'Device assigned successfully')
      }
      onSuccess?.()
    } catch (error: any) {
      if (error.response?.status === 422 && error.response?.data?.errors) {
        const validationErrors = error.response.data.errors
        const errorMessages: string[] = []
        Object.keys(validationErrors).forEach((field) => {
          if (Array.isArray(validationErrors[field])) {
            errorMessages.push(...validationErrors[field])
          } else {
            errorMessages.push(validationErrors[field])
          }
        })
        showError({ message: errorMessages.join(', ') })
      } else {
        showError(error)
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Box as="form" onSubmit={handleSubmit(onSubmit)}>
      <VStack spacing={4} align="stretch">
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Assigned User <Text as="span" color="red.500">*</Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.user_id}>
            <Box position="relative" ref={dropdownRef}>
              <Input
                placeholder="Type 3+ characters to search user..."
                value={userSearch}
                onChange={(e) => {
                  const v = e.target.value
                  setUserSearch(v)
                  setSelectedUser(null)
                  setValue('user_id', undefined as any, { shouldValidate: true })
                  clearTimeout(searchTimeoutRef.current ?? undefined)
                  searchTimeoutRef.current = setTimeout(() => runUserSearch(v), 300)
                  setShowDropdown(true)
                }}
                onFocus={() => userResults.length > 0 && setShowDropdown(true)}
              />
              {userSearching && (
                <Box position="absolute" top="100%" left={0} right={0} zIndex={2} bg="white" shadow="md" p={2}>
                  <HStack justify="center"><Spinner size="sm" /></HStack>
                </Box>
              )}
              {showDropdown && userResults.length > 0 && !userSearching && (
                <List
                  position="absolute"
                  top="100%"
                  left={0}
                  right={0}
                  zIndex={10}
                  bg="white"
                  border="1px"
                  borderColor="gray.200"
                  borderRadius="md"
                  shadow="lg"
                  maxH="240px"
                  overflowY="auto"
                >
                  {userResults.map((u) => (
                    <ListItem
                      key={u.id}
                      px={3}
                      py={2}
                      cursor="pointer"
                      _hover={{ bg: 'gray.50' }}
                      onClick={() => onUserSelect(u)}
                    >
                      {userLabel(u) || u.user_login}
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
            <FormHelperText>Search by name, employee ID, or email.</FormHelperText>
            <FormErrorMessage>{errors.user_id?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Serial No <Text as="span" color="red.500">*</Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.serial_no}>
            <Input {...register('serial_no')} placeholder="e.g. SN123456789" />
            <FormErrorMessage>{errors.serial_no?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Android ID
          </FormLabel>
          <FormControl isInvalid={!!errors.android_id}>
            <Input {...register('android_id')} placeholder="e.g. 1234567890abcdef" />
            <FormErrorMessage>{errors.android_id?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Model <Text as="span" color="red.500">*</Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.model}>
            <Input {...register('model')} placeholder="e.g. Galaxy S21, iPhone 13" />
            <FormErrorMessage>{errors.model?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Brand <Text as="span" color="red.500">*</Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.brand}>
            <Input {...register('brand')} placeholder="e.g. Samsung, Apple" />
            <FormErrorMessage>{errors.brand?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            OS
          </FormLabel>
          <FormControl isInvalid={!!errors.os}>
            <Input {...register('os')} placeholder="e.g. Android, iOS" />
            <FormErrorMessage>{errors.os?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            OS Version
          </FormLabel>
          <FormControl isInvalid={!!errors.os_version}>
            <Input type="number" {...register('os_version', { valueAsNumber: true })} placeholder="e.g. 12" />
            <FormErrorMessage>{errors.os_version?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            RAM Size
          </FormLabel>
          <FormControl isInvalid={!!errors.ram_size}>
            <Input {...register('ram_size')} placeholder="e.g. 8GB" />
            <FormErrorMessage>{errors.ram_size?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Storage Size
          </FormLabel>
          <FormControl isInvalid={!!errors.storage_size}>
            <Input {...register('storage_size')} placeholder="e.g. 128GB" />
            <FormErrorMessage>{errors.storage_size?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Active
          </FormLabel>
          <FormControl>
            <Switch isChecked={active} onChange={(e) => setValue('active', e.target.checked)} />
            <FormHelperText>Inactive devices are hidden from selection.</FormHelperText>
          </FormControl>
        </Grid>

        <HStack spacing={4} justify="flex-end" pt={4}>
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel} isDisabled={isSubmitting}>
              Cancel
            </Button>
          )}
          <Button
            type="submit"
            colorScheme="blue"
            isLoading={isSubmitting}
            loadingText={isEditMode ? 'Saving...' : 'Assigning...'}
          >
            {isEditMode ? 'Save' : 'Assign Device'}
            {isSubmitting && (
              <Text as="span" ml={2} fontSize="sm">
                ...
              </Text>
            )}
          </Button>
        </HStack>
      </VStack>
    </Box>
  )
}
